import { useRef, useEffect } from 'react';

import { useAppContext } from '../contexts/Context';
import StyleDto from '../../../shared/style.dto';

const setStyles = ({
  value,
  property,
  selector,
  componentStyles,
  componentSelectorStyles,
}: {
  value: string,
  property: string,
  selector?: string,
  componentStyles: Record<string, string>,
  componentSelectorStyles: Record<string, Record<string, string>>,
}) => {
  if (selector) {
    // eslint-disable-next-line no-param-reassign
    componentSelectorStyles[selector] = componentSelectorStyles[selector] || {};
    // eslint-disable-next-line no-param-reassign
    componentSelectorStyles[selector][property] = value;
    return;
  }

  // convert css property to camelCase as it is used with style prop
  const cssPropertyToCamelCase = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  // eslint-disable-next-line no-param-reassign
  componentStyles[cssPropertyToCamelCase] = value;
};

export function getContentStyles({ stylesProp, windowWidth }: {
  windowWidth: number,
  stylesProp: StyleDto[]
}) {
  let correctBreakPoint = -1;
  const componentStyles: Record<string, string> = {};
  const componentSelectorStyles: Record<string, Record<string, string>> = {};

  stylesProp.forEach(({
    property,
    value,
    breakPoint,
    selector,
  }) => {
    if (!property || !value) return;

    if (breakPoint !== null && windowWidth >= breakPoint && breakPoint >= correctBreakPoint) {
      correctBreakPoint = breakPoint;

      setStyles({
        value,
        property,
        selector,
        componentStyles,
        componentSelectorStyles,
      });
    } else if (breakPoint === null) {
      setStyles({
        value,
        property,
        selector,
        componentStyles,
        componentSelectorStyles,
      });
    }
  });

  return { componentStyles, componentSelectorStyles };
}

const useComponentContentStyles = ({ stylesProp = [] }: { stylesProp: StyleDto[] }) => {
  const { windowWidth } = useAppContext();
  const componentContainerRef = useRef<HTMLDivElement>(null);

  const {
    componentStyles,
    componentSelectorStyles,
  } = getContentStyles({ stylesProp, windowWidth });

  useEffect(() => {
    Object.keys(componentSelectorStyles).forEach((selector) => {
      const element = componentContainerRef
        .current?.querySelector<HTMLElement>(selector);
      if (element) {
        Object.entries(componentSelectorStyles[selector]).forEach(([property, value]) => {
          element.style.setProperty(property, value);
        });
      }
    });
  }, [windowWidth, componentStyles, componentSelectorStyles]);

  return {
    componentStyles,
    componentContainerRef,
  };
};

export default useComponentContentStyles;
